import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Bundle, HealthcareService, PractitionerRole, Reference } from 'fhir/r4';
import { forkJoin, map, Observable, of, switchMap, tap, take } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { StateService } from '../shared/state.service';
import { HealthcareServiceConversionService } from './healthcare-service-conversion.service';
import { ResourceContextOverrides } from '../shared/resource-conversion-base.service';
import { AuthService } from '../shared/auth/auth.service';
import { UtilityService } from '../shared/utility.service';

@Injectable({ providedIn: 'root' })
export class HealthcareServicesService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly stateService = inject(StateService);
  private readonly conversion = inject(HealthcareServiceConversionService);
  private readonly auth = inject(AuthService);
  private readonly utility = inject(UtilityService);

  private readonly fhirJsonHeaders = new HttpHeaders({ 'Content-Type': 'application/fhir+json' });
  private readonly practitionerRoleUrl = `${this.backendUrl}/PractitionerRole`;

  readonly healthcareServices$ = this.utility.threeTierQueryAndSyncToState$<HealthcareService>({
    resourceType: 'HealthcareService',
    scope: 'orgWide',
    count: 200
  });

  getPractitionerAssignments(serviceId: string | null | undefined): Observable<Reference[]> {
    if (!serviceId) {
      return of([]);
    }
    return this.fetchPractitionerRoles(serviceId).pipe(
      map(roles => this.uniqueReferences(
        roles
          .map(role => role.practitioner)
          .filter((ref): ref is Reference => !!ref?.reference)
      ))
    );
  }

  getHealthcareServiceById(id: string | null | undefined): HealthcareService | null {
    if (!id) {
      return null;
    }
    const reference = `HealthcareService/${id}`;
    const current = this.stateService.orgWideResources.healthcareServices.value;
    const match = current.find(entry => entry.referenceId === reference || entry.actualResource.id === id);
    return match?.actualResource ?? null;
  }

  createHealthcareService(dynamicFormData: any, overrides?: ResourceContextOverrides): Observable<HealthcareService> {
    const assignedPractitioners = this.extractAssignedPractitionerRefs(dynamicFormData?.assignedPractitioners);
    const resource = this.prepareResourceForPersistence(dynamicFormData, undefined, overrides);
    return this.http.post<HealthcareService>(`${this.backendUrl}/HealthcareService`, resource, { headers: this.fhirJsonHeaders }).pipe(
      tap(created => this.pushIntoOrgState(created)),
      switchMap(created =>
        this.syncPractitionerRoles(created, assignedPractitioners).pipe(map(() => created))
      )
    );
  }

  updateHealthcareService(existing: HealthcareService, dynamicFormData: any, overrides?: ResourceContextOverrides): Observable<HealthcareService> {
    const assignedPractitioners = this.extractAssignedPractitionerRefs(dynamicFormData?.assignedPractitioners);
    const resource = this.prepareResourceForPersistence(dynamicFormData, existing, overrides);
    if (!existing?.id) {
      throw new Error('Cannot update HealthcareService without an id');
    }
    return this.http.put<HealthcareService>(`${this.backendUrl}/HealthcareService/${existing.id}`, resource, { headers: this.fhirJsonHeaders }).pipe(
      tap(updated => this.pushIntoOrgState(updated)),
      switchMap(updated =>
        this.syncPractitionerRoles(updated, assignedPractitioners).pipe(map(() => updated))
      )
    );
  }

  deleteHealthcareService(existing: HealthcareService): Observable<void> {
    if (!existing?.id) {
      throw new Error('Cannot delete HealthcareService without an id');
    }
    return this.http.delete<void>(`${this.backendUrl}/HealthcareService/${existing.id}`).pipe(
      tap(() => this.removeFromOrgState(existing.id as string)),
      switchMap(() => this.cleanupPractitionerRoles(existing.id as string))
    );
  }

  refreshFromServer(): Observable<HealthcareService[]> {
    return this.utility.threeTierQueryAndSyncToStateOnce$<HealthcareService>({
      resourceType: 'HealthcareService',
      scope: 'orgWide',
      count: 200
    });
  }

  setHealthcareServiceActive(resource: HealthcareService, active: boolean): Observable<HealthcareService> {
    if (!resource?.id) {
      throw new Error('Cannot update HealthcareService without an id');
    }

    return this.getPractitionerAssignments(resource.id).pipe(
      take(1),
      switchMap(assignments => {
        const payload = this.conversion.eliminateEmptyFieldsInShallowAndDeepPaths({
          ...resource,
          active,
          resourceType: 'HealthcareService'
        });
        return this.http
          .put<HealthcareService>(`${this.backendUrl}/HealthcareService/${resource.id}`, payload, { headers: this.fhirJsonHeaders })
          .pipe(
            tap(updated => this.pushIntoOrgState(updated)),
            switchMap(updated => this.syncPractitionerRoles(updated, assignments).pipe(map(() => updated)))
          );
      })
    );
  }

  private prepareResourceForPersistence(
    dynamicFormData: any,
    existing?: HealthcareService | null,
    overrides?: ResourceContextOverrides
  ): HealthcareService {
    const overridesWithPractitioner: ResourceContextOverrides = {
      practitionerId: this.auth.user.getValue()?.['fhirResourceId'] || null,
      ...overrides
    };
    const converted = this.conversion.convertDynamicFormFormatToFhirResource(dynamicFormData, existing);
    const managingOrganization = this.resolveManagingOrganization(existing);
    if (managingOrganization) {
      converted.providedBy = managingOrganization;
    } else {
      delete converted.providedBy;
    }
    const withContext = this.conversion.attachAppStateContext(converted, overridesWithPractitioner);
    return this.conversion.eliminateEmptyFieldsInShallowAndDeepPaths(withContext);
  }

  private pushIntoOrgState(resource: HealthcareService): void {
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'collection',
      entry: [{ resource }]
    };
    this.stateService.processOrgWideBundleTransaction(bundle);
  }

  private removeFromOrgState(id: string): void {
    const subject = this.stateService.orgWideResources.healthcareServices;
    const filtered = subject.value.filter(entry => entry.actualResource.id !== id);
    subject.next(filtered);
  }

  private resolveManagingOrganization(existing?: HealthcareService | null): Reference | undefined {
    const orgId = this.auth.user.getValue()?.organizationId?.trim();
    if (orgId) {
      return { reference: `Organization/${orgId}` };
    }
    return existing?.providedBy ?? undefined;
  }

  private extractAssignedPractitionerRefs(raw: any): Reference[] {
    return this.uniqueReferences(
      this.utility.ensureArray(raw)
        .map(value => this.utility.referenceFromDelimited(value ?? ''))
        .filter((ref): ref is Reference => !!ref?.reference)
    );
  }

  private uniqueReferences(refs: Reference[]): Reference[] {
    const seen = new Set<string>();
    const result: Reference[] = [];
    for (const ref of refs) {
      const key = this.normalizeReference(ref);
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push({ ...ref, reference: key });
    }
    return result;
  }

  private normalizeReference(ref?: Reference | null): string | null {
    const raw = ref?.reference?.trim();
    if (!raw) {
      return null;
    }
    return raw;
  }

  private fetchPractitionerRoles(serviceId: string): Observable<PractitionerRole[]> {
    return this.http
      .get<Bundle<PractitionerRole>>(`${this.practitionerRoleUrl}?service=HealthcareService/${serviceId}&_count=200`)
      .pipe(map(bundle => (bundle.entry ?? []).map(entry => entry.resource!).filter(Boolean)));
  }

  private cleanupPractitionerRoles(serviceId: string): Observable<void> {
    return this.fetchPractitionerRoles(serviceId).pipe(
      switchMap(roles => {
        const targets = roles.filter(role => !!role.id);
        if (!targets.length) {
          return of(void 0);
        }
        const deletions = targets.map(role => this.deletePractitionerRole(role));
        return forkJoin(deletions).pipe(map(() => void 0));
      })
    );
  }

  private syncPractitionerRoles(service: HealthcareService, assignedRefs: Reference[]): Observable<void> {
    if (!service?.id) {
      return of(void 0);
    }
    const desiredAssignments = this.uniqueReferences(assignedRefs);
    return this.fetchPractitionerRoles(service.id).pipe(
      switchMap(existingRoles => {
        const operations: Observable<unknown>[] = [];
        const existingByPractitioner = this.groupRolesByPractitioner(existingRoles);
        const desiredKeys = new Set(desiredAssignments.map(ref => ref.reference!).filter(Boolean));

        for (const ref of desiredAssignments) {
          const key = ref.reference!;
          const existingSet = existingByPractitioner.get(key);
          if (!existingSet || !existingSet.length) {
            operations.push(this.createPractitionerRole(ref, service));
            continue;
          }
          const needsUpdate = existingSet.some(role => this.specialtiesDiffer(role.specialty, service.specialty));
          if (needsUpdate) {
            operations.push(...existingSet.map(role => this.updatePractitionerRole(role, service)));
          }
        }

        existingByPractitioner.forEach((roles, practitionerRef) => {
          if (!desiredKeys.has(practitionerRef)) {
            operations.push(...roles.map(role => this.deletePractitionerRole(role)));
          }
        });

        if (!operations.length) {
          return of(void 0);
        }
        return forkJoin(operations).pipe(map(() => void 0));
      })
    );
  }

  private groupRolesByPractitioner(roles: PractitionerRole[]): Map<string, PractitionerRole[]> {
    const grouped = new Map<string, PractitionerRole[]>();
    for (const role of roles) {
      const key = this.normalizeReference(role.practitioner);
      if (!key) {
        continue;
      }
      const bucket = grouped.get(key) ?? [];
      bucket.push(role);
      grouped.set(key, bucket);
    }
    return grouped;
  }

  private specialtiesDiffer(current?: PractitionerRole['specialty'], next?: HealthcareService['specialty']): boolean {
    const normalize = (collection?: any[]) =>
      (collection ?? [])
        .map(item => this.utility.delimitedFromCodeableConcept(item as any) ?? (item?.text ?? ''))
        .filter(Boolean)
        .sort()
        .join('|');
    return normalize(current) !== normalize(next as any);
  }

  private buildPractitionerRolePayload(practitioner: Reference, service: HealthcareService): PractitionerRole {
    const healthcareServiceRef: Reference = service.id
      ? { reference: `HealthcareService/${service.id}`, display: service.name }
      : { reference: service.resourceType ?? 'HealthcareService' };
    return {
      resourceType: 'PractitionerRole',
      active: service.active ?? true,
      practitioner,
      organization: service.providedBy,
      specialty: service.specialty,
      healthcareService: [healthcareServiceRef]
    } as PractitionerRole;
  }

  private createPractitionerRole(practitioner: Reference, service: HealthcareService): Observable<unknown> {
    const payload = this.buildPractitionerRolePayload(practitioner, service);
    return this.http.post<PractitionerRole>(this.practitionerRoleUrl, payload, { headers: this.fhirJsonHeaders });
  }

  private updatePractitionerRole(role: PractitionerRole, service: HealthcareService): Observable<unknown> {
    if (!role.id) {
      return of(role);
    }
    const payload: PractitionerRole = {
      ...role,
      specialty: service.specialty,
      healthcareService: [{ reference: `HealthcareService/${service.id}`, display: service.name }],
      organization: service.providedBy
    };
    return this.http.put<PractitionerRole>(`${this.practitionerRoleUrl}/${role.id}`, payload, { headers: this.fhirJsonHeaders });
  }

  private deletePractitionerRole(role: PractitionerRole): Observable<unknown> {
    if (!role.id) {
      return of(void 0);
    }
    return this.http.delete<void>(`${this.practitionerRoleUrl}/${role.id}`);
  }
}
